import {
  ManualCostSource,
  getAdditionalCost,
  getEstimateNumberValueBasedOnSettings,
  getEstimateTotal,
  manualCostSources,
  measurementSources,
} from "@Actions/estimation";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { Measurements } from "@Features/viewer-modals";
import { forwardRef, useCallback, useEffect, useState } from "react";
import { useTranslation } from "@i18n";
import { useSelector } from "react-redux";
import { RootState } from "@Reducers/index";
import type { EstimateData as EstimateDataState } from "@ServerTypes/estimates/estimates.schema";

type AddReferencesProps = {
  source: ManualCostSource;
  estimateId: string;
  fileVersionId?: string;
  rowSelected: () => void;
};

type DataSet = {
  value: number;
  name: string;
};

const AddReferences = forwardRef(({ source, estimateId, rowSelected, fileVersionId }: AddReferencesProps, ref) => {
  const { t } = useTranslation();
  const [data, setData] = useState<DataSet[] | null>(null);
  const estimateData = useSelector((state: RootState) => state.estimateData) as EstimateDataState;

  const init = useCallback(async () => {
    const [additionalCostRes, estimateTotalRes] = await Promise.all([
      getAdditionalCost({ estimateId }),
      getEstimateTotal({ estimateId }),
    ]);
    if (additionalCostRes.data.status && estimateTotalRes.data.status) {
      setData([
        ...(estimateTotalRes.data.data
          ? [
              {
                name: "Total",
                value: estimateTotalRes.data.data,
              },
            ]
          : []),
        ...((additionalCostRes.data.result ?? []) as DataSet[]),
      ]);
    }
  }, [estimateId]);

  useEffect(() => {
    if (source == manualCostSources.ADDITIONAL_COST && estimateData._id) {
      init();
    }
  }, [estimateData?._id, init, source]);

  const [columns] = useState([
    {
      field: "name",
      id: "name",
      name: t("Particular"),
      textAlign: "Left",
    },
    {
      field: "value",
      id: "value",
      name: t("Amount"),
      textAlign: "Left",
      _valueGetter: (params: DataSet) =>
        getEstimateNumberValueBasedOnSettings(Number(params.value), estimateData, "amount"),
    },
  ]);

  return (
    <div className="w-full h-100">
      {source === manualCostSources.MEASUREMENTS ? (
        <Measurements
          ref={ref}
          source={measurementSources.REFERENCE_MEASUREMENT}
          openAsPopup={true}
          estimateId={estimateId}
          handleRowSelection={rowSelected}
          fileVersionId={fileVersionId}
        />
      ) : (
        <SlickGrid1
          ref={ref as any}
          showPageCount={false}
          columnDefinitions={columns as any}
          rowHeight={40 as any}
          enableSelectAllCheckbox={true}
          dataset={data as any}
          onSelectedRowIdsChanged={rowSelected}
        />
      )}
    </div>
  );
});

AddReferences.displayName = "AddReferences";

export default AddReferences;
